import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { playSend, playReceive } from "@/lib/sound";
import { Sparkle, PaperPlaneRight, ShieldCheck, Lightning, ArrowRight, Smiley, Microphone, Paperclip, ThumbsUp, ThumbsDown } from "@phosphor-icons/react";

const GREETING = "Hi! I'm your iYeneh Assistant. Tell me what you'd like to do — call abroad, message on WhatsApp, get data for a trip, or anything else — and I'll get you set up in seconds.";

// Shared conversation used by both the full SI Sales page and the global side-panel.
export function AISalesConversation({ context = null, autoSend = null, showRecommendations = false, onNavigate = null }) {
  const [messages, setMessages] = useState([{ role: "agent", text: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [guide, setGuide] = useState(null);
  const [upgrade, setUpgrade] = useState(null);
  const doUpgrade = async () => {
    if (!upgrade?.plan_key) return;
    if (upgrade.contact_sales) { toast.info(`${upgrade.name} is set up with our team — tap Contact Sales.`); return; }
    try {
      await api.post("/ai-sales/subscribe", { plan_key: upgrade.plan_key });
      toast.success(`Upgraded to ${upgrade.name}!`);
      setUpgrade(null);
    } catch (e) { toast.error(apiError(e)); }
  };
  const [numberOptions, setNumberOptions] = useState(null);
  const [esimOptions, setEsimOptions] = useState(null);
  const navigate = useNavigate();
  const endRef = useRef(null);
  const taRef = useRef(null);
  const busyRef = useRef(false);
  const idRef = useRef(0);
  const [awaitingFirst, setAwaitingFirst] = useState(false);
  const started = messages.length > 1;
  const TA_MAX = 132; // ~4 lines
  const autoGrow = (el) => { if (!el) return; el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, TA_MAX) + "px"; };

  const [showEmoji, setShowEmoji] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [recording, setRecording] = useState(false);
  const [rated, setRated] = useState({});
  const fileRef = useRef(null);
  const recogRef = useRef(null);
  const EMOJIS = ["😀", "👍", "🙏", "🎉", "📞", "💬", "✈️", "💳", "🌍", "❤️", "🔥", "✅"];
  const pickFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input isn't supported on this browser."); return; }
    if (recording) { recogRef.current?.stop(); return; }
    const r = new SR(); r.lang = "en-US"; r.interimResults = false; r.maxAlternatives = 1;
    r.onresult = (ev) => { const t = ev.results[0][0].transcript; setInput((v) => (v ? v + " " : "") + t); };
    r.onend = () => setRecording(false);
    r.onerror = () => setRecording(false);
    recogRef.current = r; setRecording(true); r.start();
    setTimeout(() => { try { r.stop(); } catch { /* noop */ } }, 10000);
  };
  const rate = async (i, r) => {
    setRated((m) => ({ ...m, [i]: r }));
    try { await api.post("/ai-sales/feedback", { session_id: sessionId, index: i, rating: r }); }
    catch { setRated((m) => { const n = { ...m }; delete n[i]; return n; }); toast.error("Couldn't save your feedback — try again."); }
  };

  const { data: recs } = useQuery({
    queryKey: ["ai-sales-recs"],
    queryFn: async () => (await api.get("/ai-sales/recommendations")).data,
    enabled: showRecommendations,
    staleTime: 60000,
  });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, checkout, numberOptions, esimOptions]);

  const send = async (text) => {
    let msg = (text ?? input).trim();
    const media = attachment;
    if ((!msg && !media) || busyRef.current) return; // prevent duplicate submissions
    if (media) msg = `${msg ? msg + "\n" : ""}[Attached ${media.type}] ${window.location.origin}${media.url}`.trim();
    setInput("");
    if (taRef.current) taRef.current.style.height = "";
    setAttachment(null);
    setShowEmoji(false);
    setCheckout(null);
    setGuide(null);
    setNumberOptions(null);
    setEsimOptions(null);
    setMessages((m) => [...m, { role: "member", text: msg }]);
    playSend();
    busyRef.current = true;
    setBusy(true);
    setAwaitingFirst(true);

    const aiId = `sa-${++idRef.current}`;
    let created = false;
    let acc = "";
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 45000);
    try {
      const token = localStorage.getItem("nexus_token");
      const resp = await fetch(`${API}/ai-sales/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ message: msg, session_id: sessionId, origin_url: window.location.origin, context }),
        signal: ctrl.signal,
      });
      if (!resp.ok || !resp.body) throw new Error("bad response");
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let obj;
          try { obj = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (obj.delta) {
            acc += obj.delta;
            if (!created) {
              created = true;
              setAwaitingFirst(false);
              playReceive();
              setMessages((m) => [...m, { role: "agent", text: acc, id: aiId }]);
            } else {
              setMessages((m) => m.map((x) => (x.id === aiId ? { ...x, text: acc } : x)));
            }
          }
          if (obj.done) {
            const meta = obj.meta || {};
            const finalReply = obj.reply || acc;
            if (!created) {
              setAwaitingFirst(false);
              playReceive();
              setMessages((m) => [...m, { role: "agent", text: finalReply, id: aiId }]);
            } else {
              setMessages((m) => m.map((x) => (x.id === aiId ? { ...x, text: finalReply } : x)));
            }
            if (meta.session_id) setSessionId(meta.session_id);
            if (meta.checkout_url) setCheckout({ url: meta.checkout_url, amount_cents: meta.recommend_amount_cents, label: meta.checkout_label });
            if (meta.guide) setGuide(meta.guide);
            if (meta.upgrade) setUpgrade(meta.upgrade); else setUpgrade(null);
            if (meta.number_options) setNumberOptions(meta.number_options);
            if (meta.esim_options) setEsimOptions(meta.esim_options);
          }
        }
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "agent", text: "Sorry — I hit a snag. Could you say that again?" }]);
    } finally {
      clearTimeout(timer);
      busyRef.current = false;
      setBusy(false);
      setAwaitingFirst(false);
    }
  };

  const buyNumber = async (opt) => {
    try {
      const { data } = await api.post("/ai-sales/number-checkout", {
        number: opt.number, country: opt.country, number_type: opt.number_type,
        provider: opt.provider, session_id: sessionId, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const buyEsim = async (opt) => {
    try {
      const { data } = await api.post("/ai-sales/esim-checkout", {
        plan_id: opt.plan_id, country: opt.country, session_id: sessionId, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sentAuto = useRef(false);
  useEffect(() => {
    if (autoSend && !sentAuto.current) { sentAuto.current = true; send(autoSend); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSend]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto py-4" data-testid="ai-sales-messages">
        {messages.map((m, i) => (
          <div key={m.id || i} className={`flex flex-col ${m.role === "member" ? "items-end" : "items-start"}`}>
            <div data-testid={`msg-${m.role}`}
              className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === "member" ? "bg-primary text-primary-foreground" : "border border-border bg-card text-foreground"}`}>
              {m.text}
            </div>
            {m.role === "agent" && i > 0 && (
              <div className="mt-1 flex items-center gap-1.5 pl-1">
                <button data-testid={`ai-sales-fb-up-${i}`} onClick={() => rate(i, "up")} title="Helpful"
                  className={`rounded-full p-1 transition-colors ${rated[i] === "up" ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}><ThumbsUp size={14} weight={rated[i] === "up" ? "fill" : "regular"} /></button>
                <button data-testid={`ai-sales-fb-down-${i}`} onClick={() => rate(i, "down")} title="Not helpful"
                  className={`rounded-full p-1 transition-colors ${rated[i] === "down" ? "text-destructive" : "text-muted-foreground hover:text-foreground"}`}><ThumbsDown size={14} weight={rated[i] === "down" ? "fill" : "regular"} /></button>
              </div>
            )}
          </div>
        ))}
        {awaitingFirst && <div className="flex justify-start"><div className="rounded-2xl border border-border bg-card px-4 py-2.5 text-sm text-muted-foreground">Typing…</div></div>}

        {/* pre-conversation recommendations (collapse once chat starts) */}
        {showRecommendations && !started && recs && (
          <div className="space-y-4" data-testid="ai-sales-recs">
            {recs.continue && (
              <button onClick={() => send("Let's continue where we left off")} data-testid="ai-sales-continue"
                className="flex w-full items-center gap-2 rounded-2xl border border-primary/40 bg-primary/5 px-4 py-3 text-left text-sm">
                <ArrowRight size={16} className="text-primary" />
                <span className="min-w-0 flex-1">Continue where you left off</span>
              </button>
            )}
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Sparkle size={13} weight="fill" className="text-primary" /> Recommended for you</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {(recs.recommended || []).map((r) => (
                  <button key={r.key} data-testid="ai-sales-rec" onClick={() => send(r.prompt)}
                    className="rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:border-primary">
                    <p className="text-sm font-semibold">{r.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{r.sub}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Popular things I can help you buy</p>
              <div className="flex flex-wrap gap-2">
                {(recs.popular || []).map((r) => (
                  <button key={r.key} data-testid="ai-sales-popular" onClick={() => send(r.prompt)}
                    className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-foreground">{r.title}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {guide && (
          <button data-testid="ai-sales-guide" onClick={() => { navigate(guide.route); onNavigate?.(); }}
            className="flex w-full items-center justify-between rounded-2xl border border-primary/40 bg-primary/5 px-4 py-3 text-sm font-semibold text-primary transition-transform hover:scale-[1.01]">
            <span className="flex items-center gap-2"><ArrowRight size={16} weight="bold" /> {guide.label}</span>
            <span className="text-xs opacity-80">Take me there →</span>
          </button>
        )}

        {upgrade && upgrade.plan_key && (
          <button data-testid="ai-sales-upgrade" onClick={doUpgrade}
            className="flex w-full items-center justify-between rounded-2xl border border-amber-400/50 bg-amber-400/10 px-4 py-3 text-sm font-semibold text-amber-600 transition-transform hover:scale-[1.01]">
            <span className="flex items-center gap-2"><ArrowRight size={16} weight="bold" /> You've hit your plan limit — upgrade to {upgrade.name}</span>
            <span className="text-xs opacity-80">{upgrade.contact_sales ? "Contact sales →" : (upgrade.price_cents ? `$${(upgrade.price_cents / 100).toFixed(0)}/mo · one tap →` : "Upgrade →")}</span>
          </button>
        )}

        {numberOptions && numberOptions.length > 0 && (
          <div data-testid="ai-sales-numbers" className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Available numbers — pick one to buy</p>
            {numberOptions.map((n) => (
              <div key={n.number} data-testid="ai-sales-number"
                className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold tracking-wide">{n.number}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{n.country} · {n.number_type} · ${(n.monthly_price_cents / 100).toFixed(2)}/mo</p>
                </div>
                <button data-testid="ai-sales-buy-number" onClick={() => buyNumber(n)}
                  className="shrink-0 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.03]">Get this number</button>
              </div>
            ))}
            <p className="flex items-center justify-center gap-1 text-[11px] text-muted-foreground"><ShieldCheck size={13} weight="fill" /> Secure iYeneh payment · your number is provisioned automatically</p>
          </div>
        )}

        {esimOptions && esimOptions.length > 0 && (
          <div data-testid="ai-sales-esims" className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Available eSIM plans — pick one to buy</p>
            {esimOptions.map((n, i) => (
              <div key={n.plan_id || i} data-testid="ai-sales-esim"
                className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{n.label || `${n.data_gb}GB plan`}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{n.country} · {n.data_gb}GB · {n.validity_days} days · ${(n.price_cents / 100).toFixed(2)}</p>
                </div>
                <button data-testid="ai-sales-buy-esim" onClick={() => buyEsim(n)}
                  className="shrink-0 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.03]">Get this eSIM</button>
              </div>
            ))}
            <p className="flex items-center justify-center gap-1 text-[11px] text-muted-foreground"><ShieldCheck size={13} weight="fill" /> Secure iYeneh payment · your eSIM QR is issued automatically</p>
          </div>
        )}

        {checkout && (
          <div data-testid="ai-sales-checkout" className="rounded-2xl border border-primary/40 bg-primary/5 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold"><Lightning size={18} weight="fill" className="text-primary" /> {checkout.label || `Ready to add $${(checkout.amount_cents / 100).toFixed(2)} to your wallet`}</div>
            <button data-testid="ai-sales-pay" onClick={() => { window.location.href = checkout.url; }}
              className="mt-3 w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02]">Complete secure checkout</button>
            <p className="mt-2 flex items-center justify-center gap-1 text-[11px] text-muted-foreground"><ShieldCheck size={13} weight="fill" /> Secure iYeneh payment · fulfilled automatically</p>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="border-t border-border pt-3">
        {attachment && (
          <div className="mb-2 flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-xs" data-testid="ai-sales-attachment">
            {attachment.type === "image"
              ? <img src={attachment.url} alt="" className="h-8 w-8 rounded object-cover" />
              : <Paperclip size={16} className="text-primary" />}
            <span className="min-w-0 flex-1 truncate">{attachment.name}</span>
            <button data-testid="ai-sales-attachment-remove" onClick={() => setAttachment(null)} className="text-muted-foreground hover:text-destructive">✕</button>
          </div>
        )}
        {showEmoji && (
          <div className="mb-2 flex flex-wrap gap-1 rounded-xl border border-border bg-card p-2" data-testid="ai-sales-emoji">
            {EMOJIS.map((e) => (
              <button key={e} type="button" onClick={() => setInput((v) => v + e)} className="rounded-lg px-1.5 py-1 text-lg hover:bg-secondary">{e}</button>
            ))}
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); send(); }}
          className="rounded-2xl border border-border bg-card transition-colors focus-within:border-primary">
          <input ref={fileRef} type="file" accept="image/*,application/pdf,audio/*,video/*" onChange={pickFile} className="hidden" data-testid="ai-sales-file" />
          <textarea ref={taRef} data-testid="ai-sales-input" value={input}
            onChange={(e) => { setInput(e.target.value); autoGrow(e.target); }} disabled={busy}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={2}
            placeholder="Tell the SI what you need… (Enter to send, Shift+Enter for a new line)"
            style={{ maxHeight: TA_MAX }}
            className="min-h-[64px] w-full resize-none overflow-y-auto bg-transparent px-4 pt-3 text-sm leading-relaxed outline-none placeholder:text-muted-foreground" />
          <div className="flex items-center justify-between gap-1 px-2 pb-2">
            <div className="flex items-center gap-0.5">
              <button type="button" data-testid="ai-sales-attach" onClick={() => fileRef.current?.click()} title="Attach"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"><Paperclip size={18} /></button>
              <button type="button" data-testid="ai-sales-emoji-toggle" onClick={() => setShowEmoji((v) => !v)} title="Emoji"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"><Smiley size={18} /></button>
              <button type="button" data-testid="ai-sales-mic" onClick={toggleMic} title="Voice input"
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-secondary ${recording ? "animate-pulse text-destructive" : "text-muted-foreground hover:text-foreground"}`}><Microphone size={18} weight={recording ? "fill" : "regular"} /></button>
            </div>
            <button data-testid="ai-sales-send" type="submit" disabled={busy || (!input.trim() && !attachment)}
              className="flex h-10 items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.03] disabled:opacity-50">
              Send <PaperPlaneRight size={18} weight="fill" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
